import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClients } from '@/api/client';
import type { components } from '@/api/types/api-gateway';

// R7 — S13 Users screen queries.
// Backed by R2a (reads) + R2b (writes) + R2c (invitations).

export type OrgUser = components['schemas']['OrgUser'];
export type Org = components['schemas']['Org'];
export type Invitation = components['schemas']['Invitation'];

function unwrap<T>(data: T | undefined, error: unknown, what: string): T {
  if (error || !data) {
    const msg = (error as { error?: string } | undefined)?.error ?? `${what} failed`;
    throw new Error(msg);
  }
  return data;
}

export function useOrg(clients: ApiClients) {
  return useQuery({
    queryKey: ['org', 'current'],
    queryFn: async (): Promise<Org> => {
      const { data, error } = await clients.apiGateway.GET('/v1/orgs/current');
      return unwrap(data, error, 'org');
    },
    staleTime: 60_000,
  });
}

export function useOrgUsers(clients: ApiClients) {
  return useQuery({
    queryKey: ['users', 'list'],
    queryFn: async (): Promise<OrgUser[]> => {
      const { data, error } = await clients.apiGateway.GET('/v1/users');
      const body = unwrap(data, error, 'users');
      return body.items;
    },
    staleTime: 30_000,
  });
}

export function useInvitations(clients: ApiClients) {
  return useQuery({
    queryKey: ['invitations', 'list'],
    queryFn: async (): Promise<Invitation[]> => {
      const { data, error } = await clients.apiGateway.GET('/v1/invitations');
      const body = unwrap(data, error, 'invitations');
      return body.items;
    },
    staleTime: 30_000,
  });
}

export function useChangeUserRole(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string; role: string }) => {
      const { data, error } = await clients.apiGateway.PATCH('/v1/users/{userId}', {
        params: { path: { userId: vars.userId } },
        body: { role: vars.role as 'viewer' | 'maintainer' | 'manager' | 'admin' },
      });
      if (error) throw new Error('change role failed');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useDeactivateUser(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      const { data, error } = await clients.apiGateway.POST(
        '/v1/users/{userId}/deactivate',
        { params: { path: { userId: vars.userId } } },
      );
      if (error) throw new Error('deactivate failed');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useActivateUser(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      const { data, error } = await clients.apiGateway.POST(
        '/v1/users/{userId}/activate',
        { params: { path: { userId: vars.userId } } },
      );
      if (error) throw new Error('activate failed');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useInviteUser(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      email: string;
      role: string;
      displayName?: string;
      groupIds?: string[];
    }) => {
      const { data, error } = await clients.apiGateway.POST('/v1/invitations', {
        body: {
          email: vars.email,
          role: vars.role as 'viewer' | 'maintainer' | 'manager' | 'admin',
          display_name: vars.displayName,
          group_ids: vars.groupIds,
        },
      });
      if (error) throw new Error('invite failed');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invitations'] }),
  });
}
